import { lazy, Suspense, type ReactNode } from "react";

import {
  ACCOUNT_ORDER_PREFIX,
  ACCOUNT_PATH,
  ACCOUNT_VERIFY_PATH,
  LOGIN_PATH,
  REGISTER_PATH,
} from "./account-routes";
import { HomePage } from "./homepage/HomePage";
import { CONTACT_PATH } from "./public-routes";
import {
  findProductPage,
  PRODUCT_CATALOG_PATH,
  PRODUCT_PAGE_PREFIX,
} from "./product-pages/product-pages-content";
import {
  ACCESSIBILITY_PATH,
  ARTWORK_UPLOAD_ROUTE_PREFIX,
  CART_ROUTE_PATH,
  CHECKOUT_PATH,
  ORDER_CONFIRMATION_ROUTE_PATH,
  PRIVACY_PATH,
  PRODUCT_DESIGNER_PREVIEW_PATH,
  PROOF_ROUTE_PREFIX,
  PVC_SIGN_ROUTE_PATH,
} from "./storefront-routes";
import { StorefrontLoadingPage } from "./storefront-loading-page";
import { SiteHeader } from "./site-chrome";
import { WORK_GALLERY_PATH } from "./work-gallery/work-gallery-api";

const ContactPage = lazy(() =>
  import("./contact-page").then((module) => ({ default: module.ContactPage })),
);
const PrivacyPolicyPage = lazy(() =>
  import("./public-policy-page").then((module) => ({ default: module.PrivacyPolicyPage })),
);
const AccessibilityStatementPage = lazy(() =>
  import("./public-policy-page").then((module) => ({ default: module.AccessibilityStatementPage })),
);
const LoginPage = lazy(() =>
  import("./account-page").then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("./account-page").then((module) => ({ default: module.RegisterPage })),
);
const VerifyAccountPage = lazy(() =>
  import("./account-page").then((module) => ({ default: module.VerifyAccountPage })),
);
const AccountDashboardPage = lazy(() =>
  import("./account-page").then((module) => ({ default: module.AccountDashboardPage })),
);
const AccountOrderPage = lazy(() =>
  import("./account-page").then((module) => ({ default: module.AccountOrderPage })),
);
const AccountSettingsPage = lazy(() =>
  import("./account-settings-page").then((module) => ({ default: module.AccountSettingsPage })),
);
const AccountBillingPage = lazy(() =>
  import("./account-billing-page").then((module) => ({ default: module.AccountBillingPage })),
);
const AccountFilesPage = lazy(() =>
  import("./account-files-page").then((module) => ({ default: module.AccountFilesPage })),
);
const AccountSecurityRouter = lazy(() =>
  import("./account-security-page").then((module) => ({ default: module.AccountSecurityRouter })),
);
const PvcStorefrontPage = lazy(() =>
  import("./pvc-storefront-page").then((module) => ({ default: module.PvcStorefrontPage })),
);
const ProductCatalogPage = lazy(() =>
  import("./product-pages/ProductCatalogPage").then((module) => ({ default: module.ProductCatalogPage })),
);
const WorkGalleryPage = lazy(() =>
  import("./work-gallery/WorkGalleryPage").then((module) => ({ default: module.WorkGalleryPage })),
);
const GenericProductPage = lazy(() =>
  import("./product-pages/GenericProductPage").then((module) => ({ default: module.GenericProductPage })),
);
const DynamicGenericProductPage = lazy(() =>
  import("./product-pages/DynamicGenericProductPage").then((module) => ({ default: module.DynamicGenericProductPage })),
);
const ProductDesignerPreviewPage = lazy(() =>
  import("./product-designer/ProductDesignerPreviewPage").then((module) => ({ default: module.ProductDesignerPreviewPage })),
);
const CartPage = lazy(() =>
  import("./cart-page").then((module) => ({ default: module.CartPage })),
);
const CheckoutPage = lazy(() =>
  import("./checkout-page").then((module) => ({ default: module.CheckoutPage })),
);
const CustomerOrderConfirmationPage = lazy(() =>
  import("./customer-confirmation-page").then((module) => ({ default: module.CustomerOrderConfirmationPage })),
);
const ArtworkUploadPage = lazy(() =>
  import("./customer-files-page").then((module) => ({ default: module.ArtworkUploadPage })),
);
const ProofReviewPage = lazy(() =>
  import("./customer-files-page").then((module) => ({ default: module.ProofReviewPage })),
);

function normalizePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed || "/";
}

function tokenFromPath(path: string, prefix: string): string | null {
  if (!path.startsWith(prefix)) return null;
  const encoded = path.slice(prefix.length);
  if (!encoded || encoded.includes("/")) return null;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

function NotFound() {
  return (
    <div className="storefront-shell storefront-shell--warm-product" dir="rtl" lang="he">
      <SiteHeader />
      <main className="storefront-not-found">
        <div className="storefront-not-found__content">
          <span className="storefront-not-found__code" aria-hidden="true">404</span>
          <h1>העמוד לא נמצא</h1>
          <p>ייתכן שהכתובת השתנתה. אפשר לחזור לדף הבית או למצוא את המוצר שחיפשתם בקטלוג.</p>
          <div className="storefront-not-found__actions">
            <a className="storefront-primary-link" href={PRODUCT_CATALOG_PATH}>לכל המוצרים</a>
            <a className="storefront-secondary-link" href="/">לדף הבית</a>
          </div>
        </div>
      </main>
    </div>
  );
}

function LazyRoute({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<StorefrontLoadingPage message="טוענים את העמוד…" />}>
      {children}
    </Suspense>
  );
}

export function App() {
  const path = normalizePath(window.location.pathname);
  const artworkToken = tokenFromPath(path, ARTWORK_UPLOAD_ROUTE_PREFIX);
  const proofToken = tokenFromPath(path, PROOF_ROUTE_PREFIX);
  const accountOrderId = tokenFromPath(path, ACCOUNT_ORDER_PREFIX);
  const productSlug = tokenFromPath(path, PRODUCT_PAGE_PREFIX);
  const productPage = productSlug ? findProductPage(productSlug) : null;

  // Keep the homepage eager: it is the primary entry route. Everything else
  // loads on demand so route-specific account/checkout/designer/PVC code does
  // not inflate the first JavaScript payload.
  if (path === "/") return <HomePage />;

  if (path === CONTACT_PATH) return <LazyRoute><ContactPage /></LazyRoute>;
  if (path === PRIVACY_PATH) return <LazyRoute><PrivacyPolicyPage /></LazyRoute>;
  if (path === ACCESSIBILITY_PATH) return <LazyRoute><AccessibilityStatementPage /></LazyRoute>;
  if (path === LOGIN_PATH) return <LazyRoute><LoginPage /></LazyRoute>;
  if (path === REGISTER_PATH) return <LazyRoute><RegisterPage /></LazyRoute>;
  if (path === ACCOUNT_VERIFY_PATH) return <LazyRoute><VerifyAccountPage /></LazyRoute>;
  if (path === ACCOUNT_PATH) return <LazyRoute><AccountDashboardPage /></LazyRoute>;
  if (path === "/account/settings") return <LazyRoute><AccountSettingsPage /></LazyRoute>;
  if (path === "/account/billing") return <LazyRoute><AccountBillingPage /></LazyRoute>;
  if (path === "/account/files") return <LazyRoute><AccountFilesPage /></LazyRoute>;
  if (
    path === "/account/security" ||
    path === "/account/password-reset" ||
    path === "/account/password-reset/confirm" ||
    path === "/account/email-change/confirm"
  ) return <LazyRoute><AccountSecurityRouter /></LazyRoute>;
  if (accountOrderId) return <LazyRoute><AccountOrderPage orderId={accountOrderId} /></LazyRoute>;
  if (path === PVC_SIGN_ROUTE_PATH) return <LazyRoute><PvcStorefrontPage /></LazyRoute>;
  if (path === PRODUCT_CATALOG_PATH) return <LazyRoute><ProductCatalogPage /></LazyRoute>;
  if (path === WORK_GALLERY_PATH) return <LazyRoute><WorkGalleryPage /></LazyRoute>;
  if (productPage) return <LazyRoute><GenericProductPage product={productPage} /></LazyRoute>;
  if (productSlug) return <LazyRoute><DynamicGenericProductPage slug={productSlug} /></LazyRoute>;
  if (path === PRODUCT_DESIGNER_PREVIEW_PATH) {
    return <LazyRoute><ProductDesignerPreviewPage /></LazyRoute>;
  }
  if (path === CART_ROUTE_PATH) return <LazyRoute><CartPage /></LazyRoute>;
  if (path === CHECKOUT_PATH) return <LazyRoute><CheckoutPage /></LazyRoute>;
  if (path === ORDER_CONFIRMATION_ROUTE_PATH) {
    return <LazyRoute><CustomerOrderConfirmationPage /></LazyRoute>;
  }
  if (artworkToken) return <LazyRoute><ArtworkUploadPage token={artworkToken} /></LazyRoute>;
  if (proofToken) return <LazyRoute><ProofReviewPage token={proofToken} /></LazyRoute>;
  return <NotFound />;
}
